/**
 * Comportements globaux des champs de saisie (zone connectée uniquement — l'écran de connexion est exclu) :
 *  1. Majuscule automatique sur la première lettre des champs texte / textarea (saisie, collage, valeur affichée au focus).
 *  2. Auto-complétion native du navigateur désactivée (autocomplete="off" / "new-password", noms non génériques).
 * Implémenté une seule fois ici, au lieu de champ par champ.
 */
const EXCLUDED_TYPES = new Set(["email", "password", "number", "url", "tel", "date", "time", "datetime-local", "month", "week", "color", "range", "file", "checkbox", "radio", "hidden", "submit", "button"]);
const EXCLUDED_RE = /(email|mail|password|passwd|secret|key|token|url|link|login|identifiant|username|reference|numero|porte|code|qp|quote|den|num$|-num\b|search|filter|recherche|otp|slug|id$|iban|phone|tel)/i;

const isTextField = (el) => el instanceof HTMLTextAreaElement || (el instanceof HTMLInputElement && !EXCLUDED_TYPES.has((el.type || "text").toLowerCase()));

/** Champ soumis à la majuscule automatique ? (exclusions : email, identifiants, mots de passe, numériques, références, URL, clés). */
export const shouldCapitalize = (el) => {
  if (!isTextField(el) || el.readOnly || el.disabled) return false;
  if (el.dataset.noCap !== undefined || el.closest("[data-no-cap]")) return false;
  if ((el.inputMode || "").toLowerCase() === "numeric" || (el.inputMode || "").toLowerCase() === "decimal") return false;
  const hint = `${el.name || ""} ${el.id || ""} ${el.dataset.testid || ""} ${el.getAttribute("autocomplete") || ""} ${el.placeholder || ""}`;
  if (/@/.test(el.placeholder || "")) return false;
  return !EXCLUDED_RE.test(hint) || el.dataset.cap !== undefined;
};

export const capitalizeFirst = (v) => {
  const i = v.search(/\p{L}/u);
  if (i === -1) return v;
  const ch = v[i];
  return ch === ch.toLocaleUpperCase("fr-FR") ? v : v.slice(0, i) + ch.toLocaleUpperCase("fr-FR") + v.slice(i + 1);
};

/** Écrit la valeur via le setter natif (le traqueur de React voit le changement → onChange reçoit la valeur capitalisée). */
const setNativeValue = (el, next) => {
  const start = el.selectionStart, end = el.selectionEnd;
  const proto = el instanceof HTMLTextAreaElement ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype;
  Object.getOwnPropertyDescriptor(proto, "value").set.call(el, next);
  try { if (start != null) el.setSelectionRange(start, end); } catch { /* type non sélectionnable */ }
};

const onInput = (e) => {
  const el = e.target;
  if (!shouldCapitalize(el)) return;
  const next = capitalizeFirst(el.value);
  if (next !== el.value) setNativeValue(el, next);
};

/** Valeur déjà enregistrée : transformée à l'affichage lorsque le champ prend le focus (déclenche onChange React). */
const onFocus = (e) => {
  const el = e.target;
  if (!shouldCapitalize(el) || !el.value) return;
  const next = capitalizeFirst(el.value);
  if (next !== el.value) { setNativeValue(el, next); el.dispatchEvent(new Event("input", { bubbles: true })); }
};

const noAutofill = (el) => {
  if (el instanceof HTMLFormElement) { el.setAttribute("autocomplete", "off"); return; }
  if (!(el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement)) return;
  if (el.dataset.keepAutocomplete !== undefined) return;
  const type = (el.type || "text").toLowerCase();
  if (el instanceof HTMLInputElement && EXCLUDED_TYPES.has(type) && type !== "email" && type !== "password") return;
  el.setAttribute("autocomplete", type === "password" ? "new-password" : "off");
  el.setAttribute("data-form-type", "other");
  el.setAttribute("data-lpignore", "true");
  if (!el.name) el.setAttribute("name", `f-${(el.dataset.testid || "field").replace(/[^a-z0-9-]/gi, "")}-${Math.random().toString(36).slice(2, 6)}`);
};

const scan = (root) => {
  if (root instanceof HTMLElement) { noAutofill(root); root.querySelectorAll("form, input, textarea").forEach(noAutofill); }
};

/** Installe les comportements globaux ; retourne la fonction de nettoyage. */
export function installFieldBehaviors(root = document) {
  root.addEventListener("input", onInput, true);
  root.addEventListener("focusin", onFocus, true);
  scan(document.body);
  // Perf : les mutations DOM sont regroupées (un seul balayage différé par rafale) au lieu d'un balayage par nœud ajouté.
  let queued = null; const bag = new Set();
  const mo = new MutationObserver((muts) => {
    muts.forEach((m) => m.addedNodes.forEach((n) => { if (n instanceof HTMLElement) bag.add(n); }));
    if (queued || !bag.size) return;
    queued = setTimeout(() => { queued = null; const nodes = [...bag]; bag.clear(); nodes.forEach((n) => n.isConnected && scan(n)); }, 120);
  });
  mo.observe(document.body, { childList: true, subtree: true });
  return () => { root.removeEventListener("input", onInput, true); root.removeEventListener("focusin", onFocus, true); clearTimeout(queued); mo.disconnect(); };
}
